import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { Op } from 'quill/core';
import React, { useRef, useState } from 'react';
import parseBlockEmbed from '../editor/utils/parseBlockEmbed';
import HoverContentPreview from '../post/HoverContentPreview';
import { deltaOnlyContainWhitespace } from '../utils/deltaHelpers';
import Button from './Button';
import DeltaPreview from './DeltaPreview';
import * as styles from './SummaryCollapsibleContent.module.scss';

interface SummaryCollapsibleContentProps {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  maxLines: number;
  content: Op[] | null;
  typography: 'post' | 'postWithNoTitleLine';
}

const SummaryCollapsibleContent = ({
  collapsed,
  onCollapsedChange,
  maxLines,
  content,
  typography,
}: SummaryCollapsibleContentProps) => {
  const deltaContainerRef = useRef<HTMLDivElement | null>(null);

  const [canDeltaPreviewBeClamped, setCanDeltaPreviewBeClamped] =
    useState(false);

  const restrictedCollapsedContent = _.takeWhile(
    content,
    (op) => !parseBlockEmbed(op)[0],
  );

  const canBeClamped =
    canDeltaPreviewBeClamped ||
    (content?.length ?? 0) > restrictedCollapsedContent.length;

  return (
    <>
      {content && !deltaOnlyContainWhitespace(content) && (
        <div
          className={classNames(styles.editor, {
            [styles.collapsed]: collapsed,
          })}
        >
          <HoverContentPreview>
            <DeltaPreview
              content={collapsed ? restrictedCollapsedContent : content}
              maxLines={maxLines}
              typography={typography}
              disableLineClamp={!collapsed}
              onCanBeClampedChange={setCanDeltaPreviewBeClamped}
              containerRef={deltaContainerRef}
            />
          </HoverContentPreview>
          {canBeClamped && (
            <div className={styles.showMoreButton}>
              <Button
                onClick={() => onCollapsedChange(!collapsed)}
                variant="link"
                size="large"
              >
                ...show {collapsed ? 'more' : 'less'}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SummaryCollapsibleContent;
